import 'whatwg-fetch';
import { normalize } from 'normalizr';
import { defaultsDeep, set } from 'lodash';
import FileSaver from 'file-saver';

import storage from '@lib/services/storageService';
import configService from '@lib/services/configService';
import StoreInstance from '@lib/utils/StoreInstance';
import signOut from '@lib/actions/signOut';
import recentlyUsedRequest from '@lib/utils/recentlyUsed';

import { regexRoutesNoAuthRequired } from 'routes';
import App from 'containers/App';

function recentlyUsed(response) {
  recentlyUsedRequest();
  return response;
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
async function parseJSON(response) {
  return response ? response.json().then((data) => ({ data, response })) : { response };
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response, options, endpoint) {
  if (response.status >= 200 && response.status < 300) return response;

  // Option to ignore errors, for example for lookups
  if (options.ignoreError) return;

  // On IE browsers response.url is empty.
  // Using endpoint as workaround.
  if (/\/forgot-password/.test(endpoint) || /\/reset-password/.test(endpoint) || options.skipRedirectOnError) {
    throw response;
  }

  switch (response.status) {
    case 400:
      if (/\/broadcasts\/bulk\/action/.test(endpoint)) return response;
      break;
    case 401: {
      if (!/\/login/.test(endpoint) || !regexRoutesNoAuthRequired.test(window.location.pathname)) {
        StoreInstance.store.dispatch(signOut({}));
      } else {
        return response;
      }

      break;
    }
    case 402:
      window.location.href = `${process.env.REACT_APP_SITE_BASE}/billing-update`;
      break;
    case 403:
      if (/\/tokens\/validate/.test(endpoint)) return response;
      break;
    case 404:
      // FIXME need a better solution
      if (/\/offers/.test(endpoint)) return response;
      break;
    case 409:
      return response;
    case 410:
      if (/\/waitlists\/confirmOffer|\/waitlists\/declineOffer/.test(endpoint)) {
        return response;
      }
      break;
    default:
  }

  throw response;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} endpoint  The endpoint we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(endpoint, options = {}) {
  const url = /http:|https:/i.test(endpoint) ? endpoint : `${configService.getApiBasePath()}${endpoint}`;

  // configure access token
  const token = options.token || storage.getToken() || request.token;
  const defaults = {
    credentials: 'include',
    headers: {
      'x-access-token': token,
      Pragma: 'no-cache',
    },
  };

  // use json for put/post
  if (
    options.body &&
    /put|post/i.test(options.method) &&
    (!/binary|uploads/i.test(endpoint) || /mapping/i.test(endpoint))
  ) {
    defaults.headers['Content-Type'] = 'application/json';
    set(options, 'body', JSON.stringify(options.body));
  }

  if (options.contentType) {
    let fileName = '';

    switch (options.contentType) {
      case 'application/vnd.openxmlformats':
        fileName = 'file.xls';
        break;
      case 'application/pdf':
        fileName = 'file.pdf';
        break;
      default:
    }

    return fetch(url, defaultsDeep({}, options, defaults))
      .then((response) => response.blob())
      .then((blob) => {
        FileSaver.saveAs(blob, options.filename || fileName);
      });
  }

  return fetch(url, defaultsDeep({}, options, defaults))
    .then((response) => checkStatus(response, options, endpoint))
    .then(parseJSON)
    .then(({ data, response }) => {
      if (!response) {
        return;
      }
      if (!response.ok) {
        return Promise.reject(data);
      }
      return options.schema ? { response: normalize(data, options.schema), data } : data;
    })
    .then(recentlyUsed);
}
